/**
 * features/finance/Adjustments.tsx
 * ------------------------------------------------------------------
 * CREDIT NOTES AND WRITE-OFFS — reducing what a family owes without money
 * changing hands.
 *
 * Two things that look alike and are not:
 *
 *   CREDIT NOTE  the school billed something it should not have. A pupil who
 *                had already left, a subject they never took, a fee applied
 *                to the wrong child.
 *   WRITE-OFF    the debt is real and will not be collected. The family
 *                cannot pay, or chasing it costs more than it recovers.
 *
 * Kept apart on purpose. A school that writes debt off and a school that
 * should never have billed it are in different situations, and one combined
 * "adjustments" figure hides which one you are looking at — the first is a
 * collection problem, the second a billing one.
 *
 * REFUNDS ARE NOT HERE. A refund is money going back to a family, and until
 * payment vouchers exist there is nothing to move it. Recording one would
 * reduce a balance and hand over no cash, telling a family they had been
 * refunded when they had not.
 *
 * NOTHING IS EVER DELETED OR EDITED. A credit note that can vanish is exactly
 * what an audit trail exists to prevent, so a mistake is corrected by a
 * REVERSAL: a second record pointing at the first, with its own reason. Both
 * stay on the file.
 *
 * The journal behind all this — Dr income or bad debts, Cr receivables —
 * arrives with the ledger. lib/adjustments already knows the accounting
 * (`adjustmentLines`, tested); nothing posts it yet.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import {
  FileMinus, Ban, Search, Undo2, Loader2, AlertTriangle, Receipt, Info,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { getPermissions } from '@/lib/permissions';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { fmtPula, type Invoice } from '@/lib/financeCore';
import {
  ADJUSTMENT_HELP, ADJUSTMENT_LABEL, adjustableRemaining, adjustmentLabel, balanceReducingTotal,
  type Adjustment, type AdjustmentKind,
} from '@/lib/adjustments';
import { createAdjustment, reverseAdjustment, SUPPORTED_KINDS } from '@/features/finance/adjustmentWrite';
import { PageHeader, SoftCard, StatChip, glassFieldClass, btnPrimary, btnGhost, ExportBar } from '@/features/finance/ui';
import { downloadTablePdf, downloadCsv, type TableExport } from '@/lib/tableExport';

const KIND_ICON: Record<AdjustmentKind, typeof FileMinus> = {
  credit_note: FileMinus,
  write_off: Ban,
  refund: Receipt,
};

export default function Adjustments() {
  const { userData, userRoles } = useAuth();
  const perms = getPermissions(userRoles);
  const mayAdjust = perms.canManageFinanceConfig;
  const { invoices, adjustments, loading } = useFinanceData();
  const { studentById } = useData();

  const [kind, setKind] = useState<AdjustmentKind>('credit_note');
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<Invoice | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [reversing, setReversing] = useState<Adjustment | null>(null);
  const [reverseReason, setReverseReason] = useState('');

  /** Adjustments already recorded against an invoice, so the cap is right. */
  const adjustedByInvoice = useMemo(() => {
    const m = new Map<string, Adjustment[]>();
    for (const a of adjustments) {
      if (!a.invoiceId) continue;
      const arr = m.get(a.invoiceId) ?? [];
      arr.push(a);
      m.set(a.invoiceId, arr);
    }
    return m;
  }, [adjustments]);

  const alreadyAdjusted = (invoiceId: string) =>
    balanceReducingTotal(adjustedByInvoice.get(invoiceId) ?? []);

  /** How much of the picked invoice may still be credited or written off. */
  const cap = picked
    ? adjustableRemaining({ total: picked.total, paid: picked.paid }, alreadyAdjusted(picked.id))
    : 0;

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [] as Invoice[];
    return invoices
      .filter((i) => i.status !== 'void')
      .filter((i) => adjustableRemaining({ total: i.total, paid: i.paid }, alreadyAdjusted(i.id)) > 0)
      .filter((i) => `${i.invoiceNo} ${i.studentName ?? ''}`.toLowerCase().includes(q))
      .slice(0, 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, search, adjustedByInvoice]);

  const rows = useMemo(
    () => [...adjustments].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))),
    [adjustments],
  );

  const totals = useMemo(() => {
    const live = rows.filter((a) => !a.reversedBy && !a.reversesId);
    return {
      creditNotes: live.filter((a) => a.kind === 'credit_note' && !a.isDiscount).reduce((n, a) => n + a.amount, 0),
      writeOffs: live.filter((a) => a.kind === 'write_off').reduce((n, a) => n + a.amount, 0),
      reversed: rows.filter((a) => a.reversedBy).length,
    };
  }, [rows]);

  async function submit() {
    if (!picked) { setMsg({ ok: false, text: 'Pick the invoice this applies to.' }); return; }
    setBusy(true);
    setMsg(null);
    try {
      const a = await createAdjustment({
        kind,
        invoice: { id: picked.id },
        amount: Number(amount) || 0,
        reason,
        date,
        actor: { uid: userData?.uid, name: userData?.displayName, email: userData?.email },
      });
      setMsg({ ok: true, text: `${ADJUSTMENT_LABEL[kind]} ${a.adjustmentNo} recorded — ${fmtPula(a.amount)} off ${a.invoiceNo}.` });
      setPicked(null); setAmount(''); setReason(''); setSearch('');
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally { setBusy(false); }
  }

  async function doReverse() {
    if (!reversing) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await reverseAdjustment(reversing, reverseReason, {
        uid: userData?.uid, name: userData?.displayName, email: userData?.email,
      });
      setMsg({ ok: true, text: `${reversing.adjustmentNo} reversed by ${r.adjustmentNo}. Both stay on the record.` });
      setReversing(null); setReverseReason('');
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally { setBusy(false); }
  }

  const table: TableExport<Adjustment> = {
    title: 'Credit notes and write-offs',
    columns: [
      { header: 'Number', text: (a) => a.adjustmentNo },
      { header: 'Date', text: (a) => a.date },
      { header: 'Kind', text: (a) => adjustmentLabel(a) },
      { header: 'Pupil', text: (a) => a.studentName ?? studentById(a.studentId)?.fullName ?? a.studentId },
      { header: 'Invoice', text: (a) => a.invoiceNo ?? '—' },
      { header: 'Amount', text: (a) => fmtPula(a.amount), raw: (a) => a.amount, align: 'right' },
      { header: 'Reason', text: (a) => a.reason },
      { header: 'Status', text: (a) => (a.reversedBy ? 'Reversed' : a.reversesId ? 'Reversal' : 'Applied') },
    ],
    rows,
  };

  if (loading) return <div className="flex justify-center py-16"><Spinner label="Loading invoices…" /></div>;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FileMinus}
        title="Credit Notes & Write-offs"
        subtitle="Reduce what a family owes without money changing hands — every one with a reason, none of them deletable"
        actions={
          <ExportBar
            disabled={rows.length === 0}
            onPdf={() => downloadTablePdf(table)}
            onCsv={() => downloadCsv(table)}
          />
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatChip tone="amber" value={fmtPula(totals.creditNotes)} label="Credited — should not have been billed" icon={FileMinus} />
        <StatChip tone="red" value={fmtPula(totals.writeOffs)} label="Written off — real but uncollectable" icon={Ban} />
        <StatChip tone="slate" value={String(totals.reversed)} label="Reversed" icon={Undo2} />
      </div>

      {msg && (
        <p className={`text-sm ${msg.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-600 dark:text-red-400'}`}>
          {msg.text}
        </p>
      )}

      {/* ── raise one ─────────────────────────────────────────── */}
      {mayAdjust && (
        <SoftCard>
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-3">Record an adjustment</h3>

          <div className="flex flex-wrap gap-2 mb-3">
            {SUPPORTED_KINDS.map((k) => {
              const Icon = KIND_ICON[k];
              const on = k === kind;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => { setKind(k); setMsg(null); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${
                    on ? 'border-primary text-primary dark:text-primarySoft bg-primary/5' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                  }`}
                >
                  <Icon size={15} /> {ADJUSTMENT_LABEL[k]}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{ADJUSTMENT_HELP[kind]}</p>

          <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-2.5 text-gray-400" />
              <input
                value={picked ? `${picked.invoiceNo} · ${picked.studentName ?? ''}` : search}
                onChange={(e) => { setPicked(null); setSearch(e.target.value); }}
                placeholder="Search an invoice number or pupil…"
                className={glassFieldClass('pl-9')}
              />
              {!picked && matches.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 shadow-lg max-h-56 overflow-y-auto">
                  {matches.map((i) => (
                    <button
                      key={i.id}
                      type="button"
                      onClick={() => { setPicked(i); setSearch(''); }}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      {i.invoiceNo} · {i.studentName}
                      <span className="text-[11px] text-gray-400 ml-2">
                        {fmtPula(adjustableRemaining({ total: i.total, paid: i.paid }, alreadyAdjusted(i.id)))} outstanding
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              inputMode="decimal"
              placeholder="0.00"
              className={glassFieldClass('text-right')}
            />
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={glassFieldClass('')} />
          </div>

          {picked && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {fmtPula(cap)} outstanding on {picked.invoiceNo}.
              {' '}Nothing more than that can be adjusted — an overpayment is refunded, not credited.
            </p>
          )}

          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is the school giving this up? Kept on the record permanently."
            className={glassFieldClass('mt-3')}
          />

          <div className="flex items-center justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={submit}
              disabled={busy || !picked || !amount || reason.trim().length < 3}
              className={btnPrimary}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <FileMinus size={14} />}
              Record {ADJUSTMENT_LABEL[kind].toLowerCase()}
            </button>
          </div>
        </SoftCard>
      )}

      <SoftCard pad="p-3">
        <p className="text-xs text-gray-600 dark:text-gray-300 flex items-start gap-2">
          <Info size={14} className="mt-0.5 shrink-0 text-primary dark:text-primarySoft" />
          <span>
            A refund — money going back to a family — is not here. It needs a payment voucher to move the cash,
            and those arrive with the payables module. Recording one now would reduce a balance and pay nobody.
          </span>
        </p>
      </SoftCard>

      {/* ── the record ────────────────────────────────────────── */}
      <SoftCard pad="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2.5 font-medium">Number</th>
                <th className="px-4 py-2.5 font-medium">Date</th>
                <th className="px-4 py-2.5 font-medium">Kind</th>
                <th className="px-4 py-2.5 font-medium">Pupil</th>
                <th className="px-4 py-2.5 font-medium">Invoice</th>
                <th className="px-4 py-2.5 font-medium text-right">Amount</th>
                <th className="px-4 py-2.5 font-medium">Reason</th>
                <th className="px-4 py-2.5 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  Nothing has been credited or written off.
                </td></tr>
              )}
              {rows.map((a) => {
                const Icon = KIND_ICON[a.kind];
                const spent = !!a.reversedBy;
                return (
                  <tr key={a.id} className={`hover:bg-gray-50 dark:hover:bg-gray-800/40 ${spent ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-100">{a.adjustmentNo}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{a.date}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">
                      <span className="inline-flex items-center gap-1.5"><Icon size={13} /> {adjustmentLabel(a)}</span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{a.studentName ?? a.studentId}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{a.invoiceNo ?? '—'}</td>
                    <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${spent ? 'line-through text-gray-400' : 'text-gray-800 dark:text-gray-100'}`}>
                      {fmtPula(a.amount)}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 max-w-xs truncate" title={a.reason}>
                      {a.reason}
                      {a.reversesId && <span className="ml-2 text-[10px] font-bold uppercase text-gray-400">reversal</span>}
                      {spent && <span className="ml-2 text-[10px] font-bold uppercase text-red-500">reversed</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {mayAdjust && !spent && !a.reversesId && (!a.isDiscount || perms.canIssueDiscounts) && (
                        <button
                          type="button"
                          onClick={() => { setReversing(a); setReverseReason(''); }}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-red-600"
                        >
                          <Undo2 size={12} /> Reverse
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SoftCard>

      {/* ── reversal ──────────────────────────────────────────── */}
      {reversing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-500 mt-0.5 shrink-0" />
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white">Reverse {reversing.adjustmentNo}?</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {ADJUSTMENT_LABEL[reversing.kind]} · {fmtPula(reversing.amount)} · {reversing.invoiceNo}
                </p>
              </div>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm text-gray-700 dark:text-gray-200">
              <p>
                {fmtPula(reversing.amount)} goes back onto {reversing.invoiceNo}, so the family owes it again.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                The original is not deleted or edited — it happened. This writes a second record pointing at it,
                and both stay on the file.
              </p>
              <input
                autoFocus
                value={reverseReason}
                onChange={(e) => setReverseReason(e.target.value)}
                placeholder="Why is it being reversed?"
                className={glassFieldClass('')}
              />
            </div>
            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setReversing(null)} disabled={busy} className={btnGhost}>Cancel</button>
              <button
                type="button"
                onClick={doReverse}
                disabled={busy || reverseReason.trim().length < 3}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40 inline-flex items-center gap-1.5"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />} Reverse it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
